import React, { useRef } from "react";
import {
  VictoryArea,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryLabelProps,
  VictoryLegend,
  VictoryLegendProps,
  VictoryLine,
  VictoryStyleInterface,
  VictoryTheme,
  VictoryTooltip,
  VictoryTooltipProps,
} from "victory";
import styled from "@emotion/styled";
import * as tokens from "../../design-tokens";
import { useContainerDimensions } from "../../utils/useContainerDimensions";
import { DashedBar } from "./CustomDashedBar";

interface ChartProps {
  barData?: Record<string, string | number | string[]>[];
  colors?: CustomColors;
  secondaryData?: Record<string, string | number>[];
  lineData?: Record<string, string | number>[];
  secondaryLineData?: Record<string, string | number>[];
  xLabel: string;
  areaData?: {
    x: number;
    y: number;
    y0: 0;
  }[];
  secondaryAreaData?: {
    x: number;
    y: number;
    y0: 0;
  }[];
  yTickFormat?: (val: number) => string;
  xLabelFormat?: (val: number | string) => string;
  showXGrid?: boolean;
  howManyXAxis?: number;
  defaultXTickValues?: string[];
  defaultYTickValues?: string[];
  labelAngle?: number;
  testId?: string;
  animationDuration?: number;
  legendData?: VictoryLegendProps["data"];
  width?: number;
  height?: number;
  styles?: Record<string, VictoryStyleInterface>;
  primaryLineLabels?: string[];
  secondaryLineLabels?: string[];
  headerLabel?: string;
  headerValue?: string;
  barRatio?: number;
  domainPadding?: number;
  toolTipAlignment?: number;
  toolTipFontSize?: number;
  toolTipPointerWidth?: number;
  toolTipPointerLength?: number;
  toolTipContained?: boolean;
  useToolTip?: boolean;
  toolTipProps?: VictoryTooltipProps;
  toolTipLabelProps?: VictoryLabelProps;
}

interface CustomColors {
  barData?: string;
  secondaryBarDataDash?: string;
  secondaryBarDataSpace?: string;
  backgroundEmpty?: string;
  backgroundData?: string;
  dataLine?: string;
  secondaryDataLine?: string;
  legendLabels?: string;
  legendData?: string;
}

const Chart = ({
  barData = [],
  colors,
  secondaryData = [],
  lineData = [],
  secondaryLineData = [],
  primaryLineLabels = ["Primary"],
  secondaryLineLabels = ["Secondary"],
  xLabel,
  yTickFormat = (x) => `${x}`,
  xLabelFormat = (y) => `${y}`,
  areaData = [],
  secondaryAreaData = [],
  showXGrid,
  howManyXAxis,
  defaultXTickValues,
  defaultYTickValues,
  labelAngle,
  legendData,
  testId = "",
  animationDuration = 300,
  width,
  height,
  styles,
  headerLabel,
  headerValue,
  barRatio = 0.5,
  domainPadding = 10,
  toolTipAlignment = -50,
  toolTipContained = true,
  toolTipFontSize = 8,
  toolTipPointerWidth = 16,
  toolTipPointerLength = 8,
  useToolTip = false,
  toolTipProps,
  toolTipLabelProps,
}: ChartProps) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth, height: containerHeight } =
    useContainerDimensions(componentRef);

  const widthToRender = width || containerWidth;
  const heightToRender = height || containerHeight;

  const isEmpty =
    barData.length === 0 &&
    secondaryData.length === 0 &&
    lineData?.length === 0 &&
    secondaryLineData?.length === 0;

  const hasHeader = headerLabel || headerValue;

  if (hasHeader && legendData?.length) {
    console.warn("Chart header and legend will likely overlap");
  }
  const topPadding = legendData?.length ? 50 : hasHeader ? 40 : 20;

  return (
    <ChartBackground
      isEmpty={isEmpty}
      backgroundEmptyColor={colors?.backgroundEmpty}
      backgroundDataColor={colors?.backgroundData}
      ref={componentRef}
      data-testid={testId}
    >
      <VictoryChart
        domainPadding={{ y: domainPadding, x: domainPadding }}
        theme={VictoryTheme.material}
        width={widthToRender}
        height={Math.max(heightToRender - 10, 0)}
        padding={{
          top: topPadding,
          bottom: 30,
          left: 70,
          right: 15,
        }}
      >
        {hasHeader && (
          <>
            {headerLabel && (
              <VictoryLabel
                data-testid="chart-header-label"
                x={10}
                y={10}
                text={headerLabel}
                style={{
                  fill: tokens.OFFWHITE_70,
                  fontSize: "8px",
                }}
              />
            )}
            {headerValue && (
              <VictoryLabel
                data-testid="chart-header-value"
                x={10}
                y={25}
                text={headerValue}
                style={{
                  fill: tokens.OFFWHITE_70,
                  fontSize: "20px",
                }}
              />
            )}
          </>
        )}
        {legendData?.length && (
          <VictoryLegend
            x={10}
            y={10}
            data-testid="chart-legend"
            centerTitle
            orientation="horizontal"
            gutter={20}
            style={{
              labels: {
                fill: colors?.legendLabels || tokens.BRAND_OFFWHITE,
                fontFamily: `${tokens.ROOBERT}, sans-serif`,
              },
              data: { fill: colors?.legendData || tokens.BRAND_OFFWHITE },
              ...styles?.legend,
            }}
            data={legendData}
          />
        )}
        <VictoryAxis
          style={{
            ticks: { size: 0 },
            tickLabels: {
              angle: labelAngle ?? 0,
              fill: isEmpty ? tokens.OFFWHITE_40 : tokens.WHITE,
              fontFamily: `${tokens.ROOBERT}, sans-serif`,
            },
            grid: {
              strokeWidth: 1,
              strokeDasharray: "3,4",
              stroke: isEmpty ? tokens.OFFWHITE_40 : tokens.OFFWHITE_30,
            },
            axis: {
              stroke: isEmpty ? tokens.OFFWHITE_40 : tokens.OFFWHITE_30,
            },
            axisLabel: { fontFamily: `${tokens.ROOBERT}, sans-serif` },
            ...styles?.xAxis,
          }}
          animate={{
            duration: animationDuration,
            onLoad: { duration: animationDuration },
          }}
          tickValues={defaultXTickValues ?? null}
          tickFormat={xLabelFormat}
        />
        <VictoryAxis
          crossAxis={false}
          dependentAxis
          tickCount={howManyXAxis ?? 3}
          animate={{
            duration: animationDuration,
            onLoad: { duration: animationDuration },
          }}
          tickFormat={yTickFormat}
          tickValues={defaultYTickValues ?? null}
          style={{
            ticks: { size: 0 },
            tickLabels: {
              fill: isEmpty ? tokens.OFFWHITE_40 : tokens.WHITE,
              fontFamily: `${tokens.ROOBERT}, sans-serif`,
            },
            grid: showXGrid
              ? {
                  strokeWidth: 1,
                  strokeDasharray: "3,4",
                  stroke: isEmpty
                    ? tokens.OFFWHITE_40
                    : tokens.TINTS_CHARGE_BLUE_30,
                }
              : { strokeWidth: 0 },
            axis: {
              stroke: "transparent",
            },
            ...styles?.yAxis,
          }}
        />
        {lineData.length > 0 ? (
          <VictoryLine
            data-testid="primary-line-chart"
            interpolation={"natural"}
            data={lineData}
            labels={primaryLineLabels}
            style={{
              data: {
                stroke: colors?.dataLine || tokens.OFFWHITE_70,
                strokeWidth: 3,
                strokeDasharray: "10,10",
              },
              ...styles?.primaryLine,
            }}
          />
        ) : null}
        {secondaryLineData.length > 0 ? (
          <VictoryLine
            data-testid="secondary-line-chart"
            interpolation={"natural"}
            data={secondaryLineData}
            labels={secondaryLineLabels}
            style={{
              data: {
                stroke: colors?.secondaryDataLine || "#A5C9FF",
                strokeWidth: 3,
              },
              ...styles?.secondaryLine,
            }}
          />
        ) : null}
        {areaData?.length > 0 ? (
          <VictoryArea
            data-testid="area-chart"
            style={{
              data: {
                stroke: tokens.BRAND_OFFWHITE,
                strokeWidth: 2,
                fillOpacity: 0.3,
                fill: tokens.CHARGEBLUE_40,
                strokeDasharray: "8,6",
              },
              ...styles?.area,
            }}
            animate={{
              duration: animationDuration * 2,
              onLoad: { duration: animationDuration * 2 },
            }}
            interpolation="natural"
            data={areaData}
          />
        ) : null}
        {secondaryAreaData?.length > 0 ? (
          <VictoryArea
            data-testid="secondary-area-chart"
            style={{
              data: {
                stroke: tokens.BRAND_CHARGEBLUE,
                strokeWidth: 2,
                fillOpacity: 0.1,
                fill: tokens.CHARGEBLUE_40,
                strokeDasharray: "8,6",
              },
            }}
            animate={{
              duration: animationDuration * 2,
              onLoad: { duration: animationDuration * 2 },
            }}
            interpolation="natural"
            data={secondaryAreaData}
          />
        ) : null}
        {barData.length > 0 ? (
          <VictoryBar
            labelComponent={
              useToolTip ? (
                <VictoryTooltip
                  constrainToVisibleArea={toolTipContained}
                  style={{
                    fill: tokens.HEROBLUE_20,
                    fontSize: `${toolTipFontSize}px`,
                  }}
                  flyoutStyle={{
                    strokeWidth: 10,
                    stroke: tokens.TINTS_INFO_80,
                    fill: tokens.TINTS_INFO_80,
                  }}
                  pointerWidth={toolTipPointerWidth}
                  pointerLength={toolTipPointerLength}
                  {...toolTipProps}
                  labelComponent={
                    <VictoryLabel
                      style={[
                        {
                          fontWeight: "500",
                          fontFamily: "Roobert, sans-serif",
                          fill: tokens.HEROBLUE_20,
                          fontSize: `${toolTipFontSize}px`,
                        },
                        ...new Array(4).fill({
                          fontWeight: "300",
                          fontFamily: "Roobert, sans-serif",
                          fill: tokens.HEROBLUE_20,
                          fontSize: `${toolTipFontSize}px`,
                        }),
                      ]}
                      textAnchor={"start"}
                      dx={toolTipAlignment}
                      {...toolTipLabelProps}
                    />
                  }
                />
              ) : (
                <VictoryLabel />
              )
            }
            data-testid="primary-bar-chart"
            name="Bar-1"
            animate={{
              duration: animationDuration,
              onLoad: { duration: animationDuration },
            }}
            data={barData}
            x={xLabel}
            y="value"
            barRatio={barRatio}
            cornerRadius={{ top: 2 }}
            style={{
              data: {
                fill: colors?.barData || tokens.BRAND_CHARGEBLUE,
              },
              ...styles?.primaryBar,
            }}
          />
        ) : null}
        {secondaryData.length > 0 ? (
          <VictoryBar
            data-testid="secondary-bar-chart"
            animate={{
              duration: animationDuration,
              onLoad: { duration: animationDuration },
            }}
            data={secondaryData}
            x={xLabel}
            y="value"
            barRatio={barRatio}
            cornerRadius={{ top: 1 }}
            dataComponent={
              <DashedBar
                dashColor={colors?.secondaryBarDataDash}
                spaceColor={colors?.secondaryBarDataSpace}
              />
            }
          />
        ) : null}
      </VictoryChart>
    </ChartBackground>
  );
};

const ChartBackground = styled.div`
  height: 100%;
  width: 100%;
  background: ${({
    isEmpty,
    backgroundEmptyColor,
    backgroundDataColor,
  }: {
    isEmpty: boolean;
    backgroundEmptyColor?: string;
    backgroundDataColor?: string;
  }) =>
    isEmpty
      ? backgroundEmptyColor || tokens.OFFWHITE_70
      : backgroundDataColor || tokens.BRAND_NIGHT_DARKER};
  border-radius: 8px;
`;

export { Chart };
export type { ChartProps };
